import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "use-debounce";
import CircularProgress from "@mui/material/CircularProgress";
import Box from "@mui/material/Box";
import { useDispatch, useSelector } from "react-redux";

import api, { api_without_cancellation } from "../../api/api";
import { useDocumentModal } from "../common/CustomModal/CustomModalContext";
// import { useDocumentModal } from "./DocumentModalContext";
import incidentIcon from "../../assets/images/incident.svg";
import birthdayIcon from "../../assets/images/birthdayicon.svg";
import "./ClientSearch.css";
import {
  setInboxRefreshDocuments,
  setInboxRefreshDocumentsDueToDelete,
} from "../../Redux/inbox/actions";
import { fetchTaskDocumentPopupData } from "../../Providers/main";
import { formatCalendarDate } from "../../Utils/helper";

const USER_TYPE_KEYS = [
  "user_type1",
  "user_type2",
  "user_type3",
  "user_type4",
  "user_type5",
  "user_type6",
];
const discardTaskPopupData = () => {};

const getSelectedFirmUserIds = (defaultUserTypes, caseData) => {
  const selectedUserTypeNames = new Set(
    (defaultUserTypes || [])
      .filter((userType) => userType?.status === true)
      .map((userType) => userType?.name)
  );
  const firmUserIds = new Set();

  USER_TYPE_KEYS.forEach((userTypeKey, index) => {
    if (!selectedUserTypeNames.has(caseData?.[userTypeKey])) return;
    const firmUser = caseData?.[`firm_user${index + 1}`];
    if (firmUser?.id) firmUserIds.add(firmUser.id);
  });

  return Array.from(firmUserIds);
};

export function useClientSearch(searchDelay = 500) {
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm] = useDebounce(searchTerm, searchDelay);
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (searchTerm.length < 3 || searchTerm !== debouncedSearchTerm) {
      setSearchResults([]);
      setError(null);
      setIsLoading(false);
      return;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError(null);
    api_without_cancellation
      .get(`api/clients/search/?query=${encodeURIComponent(debouncedSearchTerm)}`, {
        signal: controller.signal,
      })
      .then((response) => {
        if (!controller.signal.aborted) setSearchResults(response.data);
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        setError(error);
        setSearchResults([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [debouncedSearchTerm, searchTerm]);

  return {
    searchTerm,
    setSearchTerm,
    searchResults,
    setSearchResults,
    isLoading,
    error,
  };
}

const SearchInput = ({
  value,
  onChange,
  placeholder,
  className = "",
  inputRef,
  id = "id-border-radius-important-popup",
}) => {
  const [isFocused, setIsFocused] = useState(false);

  return (
    <input
      ref={inputRef}
      type="text"
      name="caseSearch"
      autoComplete="off"
      className={`form-control ${className}`}
      placeholder={placeholder}
      id={id}
      value={value}
      style={{
        flex: 1,
        minWidth: 0,
        height: "25px",
        padding: "5px 10px",
        color: isFocused ? "black" : "var(--primary-25)",
        fontWeight: "600",
      }}
      onChange={onChange}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
    />
  );
};

const ClientTable = ({
  setError,
  recs,
  isLoading,
  error,
  searchTerm = "",
  fetchPageDetails,
  setLoading,
  setSearchTerm,
  setSearchResults,
  assignTaskOnLink = false,
}) => {
  const { documentData } = useDocumentModal();
  const { id: docId } = documentData;
  const { toggle, setDocumentData, refetchDocumentData } = useDocumentModal();
  const dispatch = useDispatch();
  const defaultUserTypes = useSelector((state) => state.inbox.defaultUserTypes);

  const handleMoveToCase = async (docId, caseData, onPageId) => {
    if (!docId) {
      console.error("Document ID is required");
      setError("Document ID is required");
      return;
    }

    const pageId = onPageId;

    if (!pageId) {
      console.error("No page ID provided and no default could be determined");
      setError("Page ID is necessary but was not provided");
      return;
    }

    if (!caseData?.id) {
      console.error("Case ID is not available in the provided record");

      setError("No case associated with this document");
      return;
    }

    try {
      const response = await api.post("/api/attach_doc_to_page/", {
        page_id: pageId,
        case_id: caseData.id,
        doc_id: docId,
      });

      if (assignTaskOnLink) {
        const userIds = getSelectedFirmUserIds(defaultUserTypes, caseData);
        fetchTaskDocumentPopupData(
          response.data?.docData?.for_client?.id,
          response.data?.docData?.for_case?.id,
          JSON.stringify(userIds),
          docId,
          discardTaskPopupData
        );
      }

      setLoading(true);
      fetchPageDetails(response.data?.docData?.for_case?.id);
      setDocumentData({
        ...documentData,
        for_client: { ...response.data?.docData?.for_client },
        attached_by: {
          ...response.data?.docData?.attached_by,
        },
        for_case: {
          ...response.data?.docData?.for_case,
        },
      });
      setSearchResults([]);
      setSearchTerm("");

      dispatch(setInboxRefreshDocuments(true));
      dispatch(setInboxRefreshDocumentsDueToDelete(true));
      if (typeof refetchDocumentData === "function") {
        await refetchDocumentData();
      }
      toggle();
      // hideDocumentModal();
    } catch (error) {
      console.error("Error in handleMoveToCase function:", error);
      setError(`Something went wrong with moving the document: ${error.message}`);
      setLoading(false);
    } finally {
      setLoading(false);
      // dispatch(setInboxRefreshDocuments(false));
      // dispatch(setInboxRefreshDocumentsDueToDelete(false));
    }
  };

  function alignColumnWidthsByClass(className) {
    const elements = document.querySelectorAll(`.${className}`);
    let maxWidth = 0;

    elements.forEach((el) => {
      const width = el.offsetWidth;
      if (width > maxWidth) maxWidth = width;
    });

    elements.forEach((el) => {
      el.style.width = `${maxWidth}px`;
      el.style.minWidth = `${maxWidth}px`;
      el.style.maxWidth = `${maxWidth}px`;
    });
  }

  useEffect(() => {
    if (!recs || recs.length === 0) return;

    alignColumnWidthsByClass("birthday-wrapper");
    alignColumnWidthsByClass("case-wrapper");
    alignColumnWidthsByClass("doi-wrapper");
  }, [recs]);

  const tableBody = useMemo(() => {
    if (isLoading) {
      return (
        <tr>
          <td colSpan="6" className="text-center">
            <Box sx={{ display: "flex", width: "100%" }}>
              <CircularProgress />
            </Box>
          </td>
        </tr>
      );
    } else if (error) {
      return (
        <tr>
          <td colSpan="6" className="text-center text-danger">
            Error loading data: {error.message}
          </td>
        </tr>
      );
    } else if (recs.length > 0) {
      return recs.map((rec) => (
        <tr key={rec.id}>
          <td
            id="id-document-padding-important-popup-first"
            className="text-center height-35"
            style={{
              fontWeight: "600",
              fontSize: "14px",
              color: "#000",
            }}
          >
            <div className=" d-flex align-items-center">
              <span className="p-r-5 d-flex align-items-center">
                <img
                  className="ic-19"
                  style={{
                    border: "none",
                    borderRadius: "50%",
                    backgroundColor: "white",
                  }}
                  src={
                    rec?.for_client?.profile_pic_19p ??
                    "https://simplefirm-bucket.s3.amazonaws.com/static/bp_assets/img/avatar.svg"
                  }
                />
              </span>
              {rec?.for_client?.first_name || ""} {rec?.for_client?.last_name || ""}
            </div>
          </td>
          <td
            id="id-document-padding-important-popup birthday-col"
            className="text-center height-35"
            style={{
              fontWeight: "600",
              fontSize: "14px",
              color: "#000",
            }}
          >
            <div className=" d-flex align-items-center justify-content-center">
              <span className="p-r-5 d-flex align-items-center">
                <img className="ic-15" src={birthdayIcon} />
              </span>
              <span className="d-flex align-items-center justify-content-start birthday-wrapper">
                {formatCalendarDate(rec?.for_client?.birthday || "")}
              </span>
            </div>
          </td>
          <td
            id="id-document-padding-important-popup  case-col"
            className="text-center height-35 "
            style={{
              fontWeight: "600",
              fontSize: "14px",
              color: "#000",
            }}
          >
            <div
              className=" d-flex align-items-center justify-content-center"
              style={{
                gap: "5px",
              }}
            >
              <span className="d-flex align-items-center ">
                <img className="ic-15" src={rec?.case_type?.casetype_icon ?? ""} />
              </span>
              <span className="case-wrapper d-flex align-items-center justify-content-start">
                {rec?.case_type?.name || ""}
              </span>
              <span className="d-flex align-items-center ">
                <img className="ic-15" src={incidentIcon} />
              </span>
              <span className="doi-wrapper d-flex align-items-center justify-content-start">
                {formatCalendarDate(rec?.incident_date) || ""}
              </span>
            </div>
          </td>
          <td id="id-document-padding-important-popup-last" className="text-end height-35 p-r-5">
            <button
              onClick={() => handleMoveToCase(docId, rec, 2)}
              className="btn-success height-25"
              style={{
                fontWeight: "600",
                fontSize: "14px",
              }}
            >
              Link Document to Case Generally
            </button>
          </td>
        </tr>
      ));
    } else if (searchTerm.length !== 0 && recs.length === 0) {
      return (
        <tr>
          <td colSpan="4" className="text-center">
            No Results
          </td>
        </tr>
      );
    } else {
      return <tr />;
    }
  }, [recs, isLoading, error, searchTerm, defaultUserTypes, assignTaskOnLink]);

  return (
    <table
      className="table table-earning"
      id="id-document-padding-important-popup"
      style={{ display: searchTerm.length > 0 ? "inline-table" : "none" }}
    >
      <thead>
        <tr
          style={{
            backgroundColor: "var(--primary-10)",
          }}
        >
          <th
            className="text-start height-25 td-autosize"
            style={{ backgroundColor: "var(--primary-10)", fontWeight: 600 }}
          >
            CLIENT NAME
          </th>
          <th
            className="text-center height-25 td-autosize"
            style={{ backgroundColor: "var(--primary-10)", fontWeight: 600 }}
          >
            BIRTHDAY
          </th>
          <th
            className="text-center height-25 td-autosize"
            style={{ backgroundColor: "var(--primary-10)", fontWeight: 600 }}
          >
            CASE
          </th>
          {/* <th
            className="text-center"
            style={{ backgroundColor: "var(--primary-10)", fontWeight: 600 }}
          >
            STAGE
          </th> */}
          <th
            className="text-center height-25 td-autosize"
            style={{ backgroundColor: "var(--primary-10)", fontWeight: 600 }}
          />
        </tr>
      </thead>
      <tbody>{tableBody}</tbody>
    </table>
  );
};

export { SearchInput, ClientTable };
